import React, { useState } from "react";
import { useDeleteTenantMutation } from "../../store/services/tenants";
import { useDebouncedTenantsQuery } from "../../hooks/useDebouncedTenantsQuery";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import { TenantListItem } from "../../models/users";
import toast from "react-hot-toast";

// Hooks
import { useThemeModal, useLogoModal } from "./hooks";

// Componentes
import {
  TenantsHeader,
  ThemeModal,
  LogoModal,
  DeleteTenantDialog,
  ActionBodyTemplate,
  UserCountBodyTemplate,
  ThemeBodyTemplate,
  TenantDateBodyTemplate,
  LogoBodyTemplate,
} from "./components";

export const Tenants = () => {
  const [provider, setProvider] = useState<string>();
  const [theme, setTheme] = useState<string>();
  const [tenantToDelete, setTenantToDelete] = useState<TenantListItem>();
  const navigate = useNavigate();
  
  const {
    data: tenantsData,
    isLoading,
    refetch,
  } = useDebouncedTenantsQuery({
    provider,
    theme,
  });

  const [deleteTenant, { isLoading: isLoadingDelete }] = useDeleteTenantMutation();

  // Custom hooks
  const themeModal = useThemeModal();
  const logoModal = useLogoModal();

  const hideDeleteTenantDialog = () => setTenantToDelete(undefined);

  const handleDeleteTenant = (id: string) => {
    deleteTenant(id)
      .unwrap()
      .then(() => {
        toast.success("Tenant eliminado exitosamente!");
        refetch();
      })
      .catch((error) => {
        const errorMessage = error?.data?.err || "No se pudo eliminar el tenant";
        toast.error(errorMessage);
      })
      .finally(() => hideDeleteTenantDialog());
  };

  const handleProviderClear = () => {
    setProvider("");
  };

  const handleThemeClear = () => {
    setTheme("");
  };

  return (
    <>
      <div className="p-card gap-4 p-3">
        <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-6">
          <h1 className="text-center">Gestión de Tenants</h1>
        </div>
        <DataTable
          value={Array.isArray(tenantsData) ? tenantsData : []}
          loading={isLoading || isLoadingDelete}
          header={
            <TenantsHeader
              provider={provider}
              theme={theme}
              onProviderChange={setProvider}
              onThemeChange={setTheme}
              onProviderClear={handleProviderClear}
              onThemeClear={handleThemeClear}
              onNewTenant={() => navigate("new")}
            />
          }
          tableStyle={{ minWidth: "60rem" }}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          responsiveLayout="scroll"
        >
          <Column 
            field="id" 
            header="ID" 
            style={{ width: "150px", maxWidth: "150px" }}
            className="text-sm"
          />
          <Column 
            field="provider" 
            header="Provider"
            style={{ width: "120px" }}
          />
          <Column
            field="theme"
            header="Theme"
            body={(rowData) => (
              <ThemeBodyTemplate
                rowData={rowData}
                onThemeClick={themeModal.showThemeModal}
              />
            )}
            style={{ width: "200px", maxWidth: "200px" }}
          />
          <Column
            field="logo"
            header="Logo"
            body={(rowData) => (
              <LogoBodyTemplate
                rowData={rowData}
                onLogoClick={logoModal.showLogoModal}
              />
            )}
            style={{ width: "100px" }}
          />
          <Column
            field="userCount"
            header="Usuarios"
            body={(rowData) => <UserCountBodyTemplate rowData={rowData} />}
            style={{ width: "80px" }}
          />
          <Column
            field="createdAt"
            header="Creado"
            body={(rowData) => <TenantDateBodyTemplate rowData={rowData} />}
            style={{ width: "120px" }}
          />
          <Column
            body={(rowData) => (
              <ActionBodyTemplate
                rowData={rowData}
                onEdit={(id) => navigate(`edit/${id}`)}
                onDelete={setTenantToDelete}
                onDomains={(id) => navigate(`${id}/domains`)}
              />
            )}
            header="Acciones"
            style={{ width: "120px" }}
          />
        </DataTable>
      </div>
      
      <DeleteTenantDialog
        visible={!!tenantToDelete}
        onHide={hideDeleteTenantDialog}
        tenant={tenantToDelete}
        onDelete={handleDeleteTenant}
        loading={isLoadingDelete}
      />
      
      <ThemeModal
        visible={themeModal.themeModalVisible}
        onHide={themeModal.hideThemeModal}
        theme={themeModal.selectedTheme}
        provider={themeModal.selectedThemeProvider}
      />
       
      <LogoModal
        visible={logoModal.logoModalVisible}
        onHide={logoModal.hideLogoModal}
        logo={logoModal.selectedLogo}
        provider={logoModal.selectedLogoProvider}
      />
    </>
  );
}; 